import { CallQualityFeedbackProvider } from '@/components/call-quality-feedback/call-quality-feedback-provider';
import { Splashscreen } from '@/components/splashscreen/splashscreen';
import { Toaster } from '@/components/ui/sonner';
import { clearImpersonationState } from '@/features/impersonation-session';
import { isServerError } from '@/lib/error-classification';
import amplitudeService from '@/services/amplitude';

import { nativeApi } from '@/services/native-api/native-api';
import { supabase } from '@/services/supabase';
import type { QueryClient } from '@tanstack/react-query';
import { Outlet, createRootRoute, useRouter } from '@tanstack/react-router';

import { useEffect } from 'react';

export const Route = createRootRoute<{
  queryClient: QueryClient;
}>({
  component: RootComponent,
  errorComponent: ({ error }) => {
    // Root-level boundary catches render-time exceptions above _app. Default
    // to client framing — if a backend signature shows through (e.g. a fetch
    // escapes a closer boundary), keep server framing so the status-page CTA
    // still makes sense.
    return (
      <div className="h-screen w-full animate-in fade-in duration-1000">
        <Splashscreen error={error} kind={isServerError(error) ? 'server' : 'client'} noTimeout />
      </div>
    );
  },
});

function RootComponent() {
  const router = useRouter();

  useEffect(() => {
    if (!nativeApi) {
      return;
    }

    const navigateFromAlloPath = (path: string) => {
      const url = new URL(path, 'http://localhost');
      const pathname = url.pathname;
      const search = Object.fromEntries(url.searchParams.entries());

      router.navigate({ to: pathname, search });
    };

    const subscription = nativeApi.openUrl.navigate.callback(navigateFromAlloPath);

    // Drain any allo:// URL that arrived before the renderer was ready (cold-start).
    nativeApi.openUrl.getLastAllo.callback().then((path) => {
      if (path) {
        navigateFromAlloPath(path);
      }
    });

    return () => {
      subscription?.();
    };
  }, []);

  useEffect(() => {
    const subscription = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.provider_token && session?.user.app_metadata.provider) {
        amplitudeService.logEvent('Signed In', {
          Method: session.user.app_metadata.provider,
        });
      }

      if (event === 'SIGNED_OUT') {
        clearImpersonationState();
        void router.invalidate();
        return;
      }
    });

    return () => {
      subscription.data.subscription.unsubscribe();
    };
  }, []);

  return (
    <div className="h-dvh w-screen overflow-y-auto">
      <CallQualityFeedbackProvider>
        <Outlet />
        <Toaster position="top-right" className="flex justify-center" />
      </CallQualityFeedbackProvider>
    </div>
  );
}
